import { type Resource } from 'types/api'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useApiClient } from 'hooks/useApiClient'
import { type FileImageRequest } from 'types/fileUpload'

interface UsePutFileProps {
  id: number
  onSuccess?: (data: Resource, variables: FileImageRequest) => void
  onError?: (error: Error, variables: FileImageRequest) => void
}

export const usePutFileResource = ({
  id,
  onSuccess,
  onError,
}: UsePutFileProps) => {
  const putAPIClient = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (resource: FileImageRequest) => {
      // Get the last element of the array after splitting by dot to get the file extension
      const splitFileName = resource.ImageFileName.split('.')
      const fileUploadType = splitFileName[splitFileName.length - 1]

      const body = new FormData()
      body.set('FileUpload', resource.ImageFile)
      body.set('FileUploadName', resource.ImageFileName)
      body.set('FileUploadType', fileUploadType)

      const { data, error } = await putAPIClient.put(
        '/admin/resources/{id}/uploadfile',
        {
          params: { path: { id } },
          body: {},
          bodySerializer: () => body,
        }
      )
      return { data, error }
    },
    onSuccess: async ({ data, error }, variables: FileImageRequest) => {
      await queryClient.invalidateQueries({
        queryKey: ['/admin/resources', id],
      })
      if (error !== undefined) {
        onError?.(error as Error, variables)
      } else {
        onSuccess?.(data as Resource, variables)
      }
    },
    onError,
  })
}
